import { type FilterOptions, type ListingStatus, type PropertyType } from '@resolute/schema';
import { Button, cn, Input, MultiSelect, SearchableSelect, Select, Skeleton, type SelectOption } from '@resolute/ui';
import { useEffect, useState } from 'react';

import { type SearchMeta } from '../api';
import { type SearchState } from '../search-query';
import { type UseSearchState } from '../use-search-state';

interface FilterPanelProps extends Pick<UseSearchState, 'setFilters' | 'toggleArrayValue' | 'clear'> {
  state: SearchState;
  facets?: SearchMeta['facets'];
  /** Filter choices from `GET /filter-options` — the API is the sole source
   *  of truth here (no hardcoded fallback list), so the panel shows a
   *  loading/error state rather than stale or duplicated data. */
  options?: FilterOptions;
  optionsLoading: boolean;
  optionsError: boolean;
  onRetryOptions: () => void;
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="border-t border-border py-5 first:border-t-0 first:pt-0">
      <h3 className="mb-3 text-sm font-semibold text-foreground">{title}</h3>
      {children}
    </div>
  );
}

/** Past this many options, scrolling to find one beats searching for it. */
const SEARCH_THRESHOLD = 10;

/**
 * A single-select filter that swaps in {@link SearchableSelect} once its
 * option list passes {@link SEARCH_THRESHOLD} — a native <select> (used
 * below that) can't host a search box inside its own dropdown.
 */
function SingleSelectField({
  ariaLabel,
  placeholder,
  options,
  value,
  onChange,
}: {
  ariaLabel: string;
  placeholder: string;
  options: SelectOption[];
  value?: string;
  onChange: (value: string) => void;
}) {
  return options.length > SEARCH_THRESHOLD ? (
    <SearchableSelect
      aria-label={ariaLabel}
      placeholder={placeholder}
      options={options}
      value={value ?? ''}
      onChange={onChange}
    />
  ) : (
    <Select
      aria-label={ariaLabel}
      placeholder={placeholder}
      options={options}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

/**
 * Min/max price as local, debounced drafts rather than committing straight to
 * the URL on every keystroke — typing "5000000" used to fire 7 separate
 * requests (and, before the history fix above, 7 history entries). Also
 * blocks an invalid range (min > max) from ever reaching the API — it used to
 * sail through to a 422 that surfaced as a generic "something went wrong"
 * with no indication which filter caused it.
 */
function PriceRangeInputs({
  priceMin,
  priceMax,
  setFilters,
}: {
  priceMin?: number;
  priceMax?: number;
  setFilters: UseSearchState['setFilters'];
}) {
  const [minDraft, setMinDraft] = useState(priceMin != null ? String(priceMin) : '');
  const [maxDraft, setMaxDraft] = useState(priceMax != null ? String(priceMax) : '');

  // Stay in sync when the range changes externally (a preset button, Clear
  // all, or the back button) rather than only reflecting local typing.
  useEffect(() => setMinDraft(priceMin != null ? String(priceMin) : ''), [priceMin]);
  useEffect(() => setMaxDraft(priceMax != null ? String(priceMax) : ''), [priceMax]);

  const min = minDraft ? Number(minDraft) : undefined;
  const max = maxDraft ? Number(maxDraft) : undefined;
  const invalid = min != null && max != null && min > max;

  useEffect(() => {
    if (invalid) return;
    const id = setTimeout(() => {
      if (min !== priceMin || max !== priceMax) setFilters({ priceMin: min, priceMax: max });
    }, 400);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [min, max, invalid]);

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-end gap-3">
        <Input
          label="Min (₹)"
          type="number"
          inputMode="numeric"
          min={0}
          value={minDraft}
          onChange={(e) => setMinDraft(e.target.value)}
        />
        <Input
          label="Max (₹)"
          type="number"
          inputMode="numeric"
          min={0}
          value={maxDraft}
          onChange={(e) => setMaxDraft(e.target.value)}
        />
      </div>
      {invalid && (
        <p role="alert" className="text-xs text-danger">
          Min must be less than or equal to max.
        </p>
      )}
    </div>
  );
}

/**
 * Possession start/end as local drafts, mirroring {@link PriceRangeInputs} —
 * native date inputs don't need debouncing the same way text does, but the
 * local-draft + invalid-range guard keeps the same "never send min > max"
 * safety this panel already applies to price/area ranges.
 */
function PossessionDateInputs({
  possessionStart,
  possessionEnd,
  setFilters,
}: {
  possessionStart?: string;
  possessionEnd?: string;
  setFilters: UseSearchState['setFilters'];
}) {
  const invalid = Boolean(possessionStart && possessionEnd && possessionStart > possessionEnd);
  return (
    <div className="flex flex-col gap-3">
      <Input
        label="From"
        type="date"
        value={possessionStart ?? ''}
        onChange={(e) => setFilters({ possessionStart: e.target.value || undefined })}
      />
      <Input
        label="To"
        type="date"
        value={possessionEnd ?? ''}
        onChange={(e) => setFilters({ possessionEnd: e.target.value || undefined })}
      />
      {invalid && (
        <p role="alert" className="text-xs text-danger">
          Start date must be on or before the end date.
        </p>
      )}
    </div>
  );
}

/** Min/max RERA carpet area, mirroring {@link PriceRangeInputs}. */
function AreaRangeInputs({
  areaMin,
  areaMax,
  setFilters,
}: {
  areaMin?: number;
  areaMax?: number;
  setFilters: UseSearchState['setFilters'];
}) {
  const [minDraft, setMinDraft] = useState(areaMin != null ? String(areaMin) : '');
  const [maxDraft, setMaxDraft] = useState(areaMax != null ? String(areaMax) : '');

  useEffect(() => setMinDraft(areaMin != null ? String(areaMin) : ''), [areaMin]);
  useEffect(() => setMaxDraft(areaMax != null ? String(areaMax) : ''), [areaMax]);

  const min = minDraft ? Number(minDraft) : undefined;
  const max = maxDraft ? Number(maxDraft) : undefined;
  const invalid = min != null && max != null && min > max;

  useEffect(() => {
    if (invalid) return;
    const id = setTimeout(() => {
      if (min !== areaMin || max !== areaMax) setFilters({ areaMin: min, areaMax: max });
    }, 400);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [min, max, invalid]);

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-end gap-3">
        <Input
          label="Min (sq.ft)"
          type="number"
          inputMode="numeric"
          min={0}
          value={minDraft}
          onChange={(e) => setMinDraft(e.target.value)}
        />
        <Input
          label="Max (sq.ft)"
          type="number"
          inputMode="numeric"
          min={0}
          value={maxDraft}
          onChange={(e) => setMaxDraft(e.target.value)}
        />
      </div>
      {invalid && (
        <p role="alert" className="text-xs text-danger">
          Min must be less than or equal to max.
        </p>
      )}
    </div>
  );
}

/** Skeleton shown in place of the options-dependent sections while they load. */
function OptionsSkeleton() {
  return (
    <div className="flex flex-col gap-6 py-5">
      <Skeleton className="h-9 w-full" />
      <div className="grid grid-cols-2 gap-2.5">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-6 w-full" />
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-8 w-16" />
        ))}
      </div>
    </div>
  );
}

export function FilterPanel({
  state,
  facets,
  options,
  optionsLoading,
  optionsError,
  onRetryOptions,
  setFilters,
  toggleArrayValue,
  clear,
}: FilterPanelProps) {
  const isActivePreset = (min?: number, max?: number) =>
    state.priceMin === min && state.priceMax === max;

  return (
    <div className="flex flex-col">
      {optionsError ? (
        <div role="alert" className="flex flex-col items-start gap-2 border-b border-border py-5 text-sm">
          <p className="text-muted-foreground">Couldn&apos;t load filter options.</p>
          <Button variant="outline" onClick={onRetryOptions}>
            Try again
          </Button>
        </div>
      ) : optionsLoading || !options ? (
        <OptionsSkeleton />
      ) : (
        <>
          <Section title="Category">
            <SingleSelectField
              ariaLabel="Category"
              placeholder="All categories"
              options={options.categories}
              value={state.category}
              onChange={(v) => setFilters({ category: v || undefined })}
            />
          </Section>

          <Section title="State">
            <SingleSelectField
              ariaLabel="State"
              placeholder="All states"
              options={options.states}
              value={state.state}
              onChange={(v) => setFilters({ state: v || undefined })}
            />
          </Section>

          <Section title="City">
            <SingleSelectField
              ariaLabel="City"
              placeholder="All cities"
              options={options.cities}
              value={state.city}
              onChange={(v) => setFilters({ city: v || undefined })}
            />
          </Section>

          <Section title="Area">
            <MultiSelect
              aria-label="Area"
              placeholder="All areas"
              options={options.areas}
              value={state.areas}
              onToggle={(v) => toggleArrayValue('areas', v)}
            />
          </Section>

          <Section title="Developer">
            {/* CPzone's developer list runs into the thousands — a single
                searchable combobox handles that volume far better than
                thousands of individually-rendered checkboxes would. */}
            <SingleSelectField
              ariaLabel="Developer"
              placeholder="All developers"
              options={options.developers}
              value={state.developer}
              onChange={(v) => setFilters({ developer: v || undefined })}
            />
          </Section>

          <Section title="Property type">
            <MultiSelect
              aria-label="Property type"
              placeholder="All property types"
              options={options.propertyTypes.map((opt) => {
                const count = facets?.type?.[opt.value as PropertyType];
                return { ...opt, hint: count != null ? String(count) : undefined };
              })}
              value={state.type}
              onToggle={(v) => toggleArrayValue('type', v)}
            />
          </Section>

          <Section title="Configuration">
            <MultiSelect
              aria-label="Configuration"
              placeholder="All configurations"
              options={options.bhkConfigs}
              value={state.config}
              onToggle={(v) => toggleArrayValue('config', v)}
            />
          </Section>

          <Section title="RERA carpet area">
            <AreaRangeInputs areaMin={state.areaMin} areaMax={state.areaMax} setFilters={setFilters} />
          </Section>

          <Section title="Possession">
            <MultiSelect
              aria-label="Possession"
              placeholder="Any possession status"
              options={options.listingStatuses.map((opt) => {
                const count = facets?.listingStatus?.[opt.value as ListingStatus];
                return { ...opt, hint: count != null ? String(count) : undefined };
              })}
              value={state.listingStatus}
              onToggle={(v) => toggleArrayValue('listingStatus', v)}
            />
          </Section>

          <Section title="Possession date">
            <PossessionDateInputs
              possessionStart={state.possessionStart}
              possessionEnd={state.possessionEnd}
              setFilters={setFilters}
            />
          </Section>

          <Section title="Amenities">
            <MultiSelect
              aria-label="Amenities"
              placeholder="All amenities"
              options={options.amenities}
              value={state.amenities}
              onToggle={(v) => toggleArrayValue('amenities', v)}
            />
          </Section>

          <Section title="Budget">
            <div className="flex flex-col gap-3">
              <div className="flex flex-wrap gap-2">
                {options.priceBands.map((p) => {
                  const active = isActivePreset(p.min, p.max);
                  return (
                    <button
                      key={p.label}
                      type="button"
                      aria-pressed={active}
                      onClick={() =>
                        setFilters({
                          priceMin: active ? undefined : p.min,
                          priceMax: active ? undefined : p.max,
                        })
                      }
                      className={cn(
                        'rounded-md border px-3 py-1.5 text-sm transition-colors duration-base ease-out-expo',
                        active
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-border text-foreground hover:bg-muted',
                      )}
                    >
                      {p.label}
                    </button>
                  );
                })}
              </div>
              <PriceRangeInputs priceMin={state.priceMin} priceMax={state.priceMax} setFilters={setFilters} />
            </div>
          </Section>
        </>
      )}

      <div className="sticky -bottom-5 -mx-5 -mb-5 mt-4 z-40 border-t border-border bg-card px-5 py-3.5 shadow-[0_-4px_12px_rgba(0,0,0,0.06)]">
        <Button variant="outline" className="w-full" onClick={clear}>
          Clear all filters
        </Button>
      </div>
    </div>
  );
}
